import { useState, useEffect } from 'react';
import { Search, Mail, ShoppingBag } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const token = localStorage.getItem('chaleur_admin_token');

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, search]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/customers`, {
        params: { page: currentPage, limit: 20, search: search || undefined },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.customers || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-customers">
      <div>
        <h1 className="text-2xl font-light mb-2">Customers</h1>
        <p className="text-muted-foreground">Manage customer profiles</p>
      </div>

      {/* Search */}
      <div className="relative w-full max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 rounded-none"
        />
      </div>

      {/* Customers Table */}
      <Card className="rounded-none border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
            </div>
          ) : customers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-4 text-sm font-medium">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Phone</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Loyalty</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map(customer => (
                    <tr key={customer.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className="font-medium">{customer.first_name} {customer.last_name}</span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <a href={`mailto:${customer.email}`} className="flex items-center gap-2 text-primary hover:underline">
                          <Mail className="w-4 h-4" />
                          {customer.email}
                        </a>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {customer.phone || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs ${
                          customer.loyalty_tier === 'Gold' ? 'bg-yellow-100 text-yellow-800' :
                          customer.loyalty_tier === 'Silver' ? 'bg-gray-200 text-gray-800' :
                          'bg-amber-100 text-amber-800'
                        }`}>
                          {customer.loyalty_tier} • {customer.loyalty_points} pts
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No customers found
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPage(idx + 1)}
              className={`w-10 h-10 flex items-center justify-center text-sm ${
                currentPage === idx + 1 
                  ? 'bg-foreground text-primary-foreground' 
                  : 'border border-gray-200 hover:border-primary'
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
